import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { cilBan } from '@coreui/icons';
import { AltchaComponent } from '../../components/altcha/altcha.component';
import { NgForm } from '@angular/forms';

@Component({
    selector: 'app-cancel-reservation',
    templateUrl: './cancel-reservation.component.html',
    styleUrls: ['./cancel-reservation.component.scss'],
    standalone: false
})
export class CancelReservationComponent implements OnInit {
  @Input() ticketContext?: {companyName:string;reservationId:string;language?:string};

  @ViewChild(AltchaComponent) altcha?: AltchaComponent;

  icons = { cilBan };

  reservationId!: string;
  companyName!: string;
  success: boolean | null = null;
  isLoading = false;
  captchaToken: string | null = null;
  captchaReady = false;
  emailAddress = '';
  emailConfirmationSent: boolean | null = null;

  showConfirmModal = false;
  private confirmed = false;

  reservationName: string = '';

  /**
   * V133 safe fields from the cancel 200 (date, time, num_people, salon_name,
   * name, set_menu) — rendered into the confirmation email. Null when the
   * backend predates the field or the row carried nothing; the email then
   * keeps its plain body.
   */
  private cancelDetails: {
    date?: string | null;
    time?: string | null;
    num_people?: number | null;
    salon_name?: string | null;
    name?: string | null;
    set_menu?: string | null;
  } | null = null;

  /**
   * defaultplus tenants see appointment wording instead of reservation wording;
   * the cancel flow and endpoint are identical (V-company-branch). Resolved on
   * init so the page renders the right copy before the guest acts.
   */
  isDefaultPlus = false;
  private userId: string | null = null;


  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService
  ) {
    // register available langs
    const supportedLanguages = ['en', 'tr', 'es', 'el', 'de'];
    this.translate.addLangs(supportedLanguages);
    // set default
    this.translate.setDefaultLang('en');
    // use browser language if supported
    const browserLang = navigator.language.split('-')[0];
    this.translate.use(supportedLanguages.includes(browserLang) ? browserLang : 'en');
  }

  ngOnInit(): void {
    if (this.ticketContext) {
      if(this.ticketContext.language)this.translate.use(this.ticketContext.language);
      this.companyName=this.ticketContext.companyName;
      this.reservationId=this.ticketContext.reservationId;
      this.loadCompany();
      return;
    }

    this.route.paramMap.subscribe(params => {
      const company = params.get('companyName');
      const id = params.get('reservationId');

      if (company && id) {
        this.companyName = decodeURIComponent(company);
        this.reservationId = id;
        this.loadCompany();
      } else {
        console.warn('[WARN] Parametreler eksik!');
        this.success = false;
      }
    });
  }

  /** Resolve the tenant up front: caches the user id and picks the wording. */
  private loadCompany(): void {
    this.http
      .get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
        params: { company_name: this.companyName },
      })
      .subscribe({
        next: (res) => {
          this.userId = res.user_id ?? null;
          this.isDefaultPlus =
            String(res.company_type || '').trim().toLowerCase() === 'defaultplus';
        },
        error: () => { /* fail open: fall back to reservation wording */ },
      });
  }

  cancelReservation(): void {
    if (!this.captchaReady || !this.isEmailValid()) return;
    this.isLoading = true;

    if (this.userId) {
      this.postCancel(this.userId);
      return;
    }

    // userId not resolved yet (init lookup failed/slow) — fetch it now.
    this.http.get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
      params: { company_name: this.companyName }
    }).subscribe({
      next: (res) => {
        if (!res.user_id) {
          this.success = false;
          this.isLoading = false;
          return;
        }
        this.userId = res.user_id;
        this.postCancel(res.user_id);
      },
      error: (err) => {
        console.error('User ID fetch failed:', err);
        this.success = false;
        this.isLoading = false;
      }
    });
  }

  private postCancel(userId: string): void {
    const cancelUrl = `${environment.baseUrl}/post/public/users/${userId}/reservations/${this.reservationId}/cancel`;
    this.http.post<any>(cancelUrl, { captcha_token: this.captchaToken }).subscribe({
      next: (res) => {
        this.cancelDetails =
          res?.data && typeof res.data === 'object' ? res.data : null;
        this.success = true;
        this.isLoading = false;
        this.sendCancellationConfirmationEmail();
      },
      error: (err) => {
        console.error('Cancel failed:', err);
        this.success = false;
        this.isLoading = false;
        this.altcha?.reset();
      }
    });
  }

  onCancelClick(form?: NgForm): void {
    if (!this.isEmailValid()) {
      form?.form.markAllAsTouched();
      return;
    }
    if (!this.captchaReady) return;
    this.showConfirmModal = true;
  }

  confirmCancellation(decision: boolean): void {
    this.showConfirmModal = false;

    if (decision) {
      this.cancelReservation(); 
    }
  }

  isEmailValid(): boolean {
    const email = this.emailAddress.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 120;
  }

  private sendCancellationConfirmationEmail(): void {
    const companyName = this.escapeHtml(this.companyName || 'Sagel');
    const reservationId = this.escapeHtml(this.reservationId || '');
    const email = this.emailAddress.trim();
    const suffix = this.isDefaultPlus ? '_APPOINTMENT' : '';
    const subject = this.translate.instant(`CANCEL_PAGE.EMAIL_SUBJECT${suffix}`, {
      reservationId: this.reservationId,
    });
    const body = this.translate.instant(`CANCEL_PAGE.EMAIL_BODY${suffix}`, {
      companyName: this.companyName || 'Sagel',
      reservationId: this.reservationId,
    });

    const content = `
      <div style="font-family:Inter,Arial,sans-serif;background:#f4f6fb;padding:32px 16px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;margin:0 auto;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 18px 45px rgba(9,21,54,.12);">
          <tr>
            <td style="background:linear-gradient(130deg,#01030d,#08153a);padding:28px 32px;text-align:center;color:#fff;">
              <div style="font-size:12px;letter-spacing:.3em;text-transform:uppercase;opacity:.7;">${this.translate.instant(`CANCEL_PAGE.EMAIL_HEADING${suffix}`)}</div>
              <h1 style="margin:12px 0 0;font-size:26px;">${companyName}</h1>
              <div style="margin-top:6px;font-size:13px;opacity:.8;">ID ${reservationId}</div>
            </td>
          </tr>
          <tr>
            <td style="padding:32px;color:#1b2439;text-align:center;">
              <div style="width:56px;height:56px;line-height:56px;margin:0 auto 20px;border-radius:50%;background:#eafaf4;color:#12835a;font-size:28px;">&#10003;</div>
              <p style="margin:0;font-size:16px;line-height:1.65;">${this.escapeHtml(body)}</p>
${this.buildDetailsBlock()}
            </td>
          </tr>
          <tr>
            <td style="background:#fafbff;padding:18px 24px;text-align:center;font-size:12px;color:#7f8aac;letter-spacing:.25em;text-transform:uppercase;">Sagel</td>
          </tr>
        </table>
      </div>`;

    this.http.post(`${environment.baseUrl}/email_notifications/info`, {
      header: subject,
      content,
      to_addr: email,
    }).subscribe({
      next: () => {
        this.emailConfirmationSent = true;
      },
      error: (err) => {
        console.error('Cancellation confirmation email failed:', err);
        this.emailConfirmationSent = false;
      },
    });
  }

  /**
   * Detail table for the cancellation email, in the confirmation-email layout
   * (same row style as the booking confirmation). Renders only the rows the
   * cancel 200 actually carried; with no details at all the email keeps its
   * plain body — an older backend must not break the page. The set_menu
   * legacy placeholder 'default' is not a real experience pick.
   */
  private buildDetailsBlock(): string {
    const details = this.cancelDetails;
    if (!details) {
      return '';
    }

    const t = (key: string) => this.translate.instant(`EMAIL_CONFIRMATION.${key}`);
    const rows: string[] = [];
    const addRow = (label: string, value: string, first: boolean) => {
      const pad = first ? '' : 'padding-top:8px;';
      rows.push(`<tr>
                      <td style="width:40%;${pad}">${label}</td>
                      <td style="${pad}"><strong>${value}</strong></td>
                    </tr>`);
    };

    if (details.date) {
      addRow(t('DATE_LABEL'), this.escapeHtml(this.formatEmailDate(details.date)), rows.length === 0);
    }
    if (details.time) {
      addRow(t('TIME_LABEL'), this.escapeHtml(String(details.time).slice(0, 5)), rows.length === 0);
    }
    if (details.num_people) {
      addRow(
        t('PARTY_LABEL'),
        `${this.escapeHtml(String(details.num_people))} ${t('GUESTS_SUFFIX')}`,
        rows.length === 0
      );
    }
    if (details.salon_name) {
      addRow(t('SALON_LABEL'), this.escapeHtml(details.salon_name), rows.length === 0);
    }
    if (details.name) {
      addRow(t('NAME_LABEL'), this.escapeHtml(details.name), rows.length === 0);
    }
    if (details.set_menu && details.set_menu !== 'default') {
      addRow(t('EXPERIENCE_LABEL'), this.escapeHtml(details.set_menu), rows.length === 0);
    }

    if (rows.length === 0) {
      return '';
    }

    return `
              <table width="100%" cellspacing="0" cellpadding="0" style="margin:24px 0 0;">
                <tr>
                  <td style="padding:14px 18px;background:#f3f6ff;border-radius:14px;">
                    <table width="100%" cellspacing="0" cellpadding="0" style="font-size:14px;line-height:1.4;color:#1f2a44;text-align:left;">
                      ${rows.join('\n                      ')}
                    </table>
                  </td>
                </tr>
              </table>`;
  }

  /** Same locale rule as the booking confirmation email. */
  private formatEmailDate(isoDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate));
    if (!match) {
      return String(isoDate);
    }
    const [, year, month, day] = match;
    const lang = this.translate.currentLang || 'en';
    return lang === 'tr' || lang === 'de'
      ? `${day}.${month}.${year}`
      : `${day}/${month}/${year}`;
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
